import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { client } from '../../../api';
import type { OrgMember } from '../../../api';

export type DeleteMemberModalProps = {
  onClose: () => void;
  selectedMember: OrgMember;
};

/**
 * A modal for deleting a member of an organization.
 */
const DeleteMemberModal = ({ onClose, selectedMember }: DeleteMemberModalProps) => {
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const queryClient = useQueryClient();

  const submit = useMutation<{ success: boolean }, AxiosError<{ error: string }>>({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn('Training Mode: Mocking user deletion, no real request sent.');
        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      return client.post('org-members/delete', { member_id: selectedMember.id }).then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      toast.custom(() => <Toast variant="success">User deleted</Toast>);
      onClose();
    },
    onError: async (error) => {
      await queryClient.invalidateQueries({ queryKey: ['orgMembers'] });
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
    },
  });

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Remove user?</ModalTitle>
      <ModalBody>
        <Typography>
          {selectedMember.name || selectedMember.email_address} will be removed from your organization.
        </Typography>
      </ModalBody>

      <ModalFooter>
        <Button onClick={onClose} variant="tertiary" size="sm" disabled={submit.isPending}>
          Cancel
        </Button>
        <Button onClick={() => submit.mutate()} isLoading={submit.isPending} size="sm">
          Delete user
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeleteMemberModal;
